import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Beef, Egg, Fish, Leaf, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { OTHER, PRODUCE_CATEGORIES, PRODUCE_ITEMS, categoryOf } from '@/data/produce';
import { LAND_SIZE_UNITS, convertToHectares } from '@/lib/plotFormat';

export const CATEGORY_ICONS = { crops: Leaf, animals: Beef, poultry: Egg, fish: Fish };

function initialState(plot) {
  if (!plot) {
    return { category: '', item: '', otherName: '', amount: '', unit: 'hectares' };
  }
  const category = categoryOf(plot);
  const listed = PRODUCE_ITEMS[category].includes(plot.crop_type);
  const original = typeof plot.land_size_original === 'number' ? plot.land_size_original : plot.land_size;
  const amount = category === 'crops' ? original : plot.quantity;
  return {
    category,
    item: listed ? plot.crop_type : OTHER,
    otherName: listed ? '' : (plot.crop_type || ''),
    amount: amount != null ? String(amount) : '',
    unit: plot.land_size_unit || 'hectares',
  };
}

/**
 * "What do you produce": a type (crops, animals, poultry, fish), then the
 * thing itself, then how much — land area for crops, how many for the
 * rest. Used by the first-time wizard and the My Farm page.
 *
 * onSubmit gets the plot fields to send to the API.
 */
export default function ProduceForm({ plot = null, onSubmit, onCancel, submitLabel, saving = false }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState(() => initialState(plot));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isCrop = form.category === 'crops';

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = (form.item === OTHER ? form.otherName : form.item).trim();
    const amount = Number.parseFloat(form.amount);
    let error = null;
    if (!form.category) error = 'produce.errors.noCategory';
    else if (!name) error = 'produce.errors.noItem';
    else if (!(amount > 0)) error = isCrop ? 'produce.errors.noArea' : 'produce.errors.noCount';
    else if (!isCrop && !Number.isInteger(amount)) error = 'produce.errors.noCount';
    if (error) {
      toast({ title: t(error), variant: 'destructive' });
      return;
    }
    const fields = { crop_type: name, plot_name: name, produce_category: form.category };
    if (isCrop) {
      Object.assign(fields, {
        land_size: convertToHectares(form.amount, form.unit),
        land_size_original: amount,
        land_size_unit: form.unit,
        quantity: null,
      });
    } else {
      Object.assign(fields, { quantity: amount, land_size: null, land_size_original: null });
    }
    onSubmit(fields);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-base">{t('produce.categoryLabel')}</Label>
        <Select
          value={form.category}
          onValueChange={(v) => set({ category: v, item: '', otherName: '', amount: '' })}
        >
          <SelectTrigger className="h-14 text-base bg-white" data-testid="produce-category">
            <SelectValue placeholder={t('produce.categoryPlaceholder')} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {PRODUCE_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c} className="text-base py-3">{t(`produce.categories.${c}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {form.category && (
        <div className="space-y-2">
          <Label className="text-base">{t(`produce.itemLabel.${form.category}`)}</Label>
          <Select value={form.item} onValueChange={(v) => set({ item: v })}>
            <SelectTrigger className="h-14 text-base bg-white" data-testid="produce-item">
              <SelectValue placeholder={t('produce.itemPlaceholder')} />
            </SelectTrigger>
            <SelectContent className="max-h-72 bg-white">
              {PRODUCE_ITEMS[form.category].map((item) => (
                <SelectItem key={item} value={item} className="text-base py-3">
                  {item === OTHER ? t('produce.other') : item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.item === OTHER && (
            <Input
              className="h-14 text-base"
              maxLength={120}
              placeholder={t('produce.otherPlaceholder')}
              value={form.otherName}
              onChange={(e) => set({ otherName: e.target.value })}
              data-testid="produce-other-name"
            />
          )}
        </div>
      )}

      {form.category && (
        <div className="space-y-2">
          <Label className="text-base">
            {isCrop ? t('produce.areaLabel') : t(`produce.countLabel.${form.category}`)}
          </Label>
          <div className="flex gap-2">
            <Input
              type="number"
              inputMode={isCrop ? 'decimal' : 'numeric'}
              min="0"
              step={isCrop ? '0.1' : '1'}
              className="h-14 text-base flex-1"
              placeholder={isCrop ? t('farm.form.sizePlaceholder') : t('produce.countPlaceholder')}
              value={form.amount}
              onChange={(e) => set({ amount: e.target.value })}
              data-testid="produce-amount"
            />
            {isCrop && (
              <Select value={form.unit} onValueChange={(v) => set({ unit: v })}>
                <SelectTrigger className="h-14 w-36 text-base bg-white" data-testid="produce-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LAND_SIZE_UNITS.map((u) => (
                    <SelectItem key={u.value} value={u.value}>{t(`farm.form.units.${u.value}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 h-12">
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={saving}
          className="flex-1 h-12 text-base bg-farm-primary hover:bg-farm-primary-dark"
          data-testid="produce-save"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** "2 hectares", "40 birds" ... for a produce card or list line. */
export function produceAmount(plot, t, formatSize) {
  const category = categoryOf(plot);
  if (category === 'crops') return formatSize(plot).primary;
  return t(`produce.count.${category}`, { count: plot.quantity || 0 });
}
